import {useMutation} from "@apollo/client";
import {ReactNode, useState} from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import {FiAlertTriangle} from "react-icons/fi";
import {GoCopy} from "react-icons/go";
import {useLocation} from "react-router";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {RadioGroup} from "@/components/form/RadioGroup.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {
  DropdownAction,
  DropdownActionItem,
  DropdownItem,
  DropDownLink,
  DropDownReactLink,
} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {GQL_CHECKS_QUERY2, GQL_DELETE_CHECKS, GQL_UPDATE_CHECK_IS_PAUSED} from "@/lib/queries/checks.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {GraphQLResponse, ResponseStatus} from "@/lib/types/common.ts";
import {CheckDeleteDialogBody} from "@/pages/checks/components/actions/CheckDeleteDialogBody.tsx";
import {baseSections, QUERY} from "@/pages/checks/constants.ts";

type ChecksActionsProps = {
  id: number;
  name: string;
  isPaused: boolean;
  isUp: boolean;
  serviceType: MonitoringServiceType;
  hasWriteAccess: boolean;
  hasAdminAccess: boolean;
};

export const CheckActions = ({
  id,
  name,
  serviceType,
  isPaused,
  isUp,
  hasWriteAccess,
  hasAdminAccess,
}: ChecksActionsProps) => {
  const location = useLocation();
  const {brand, userHasFeature} = useCurrentUserContext();

  const onMutationError = () => toast.error("Something went wrong, please contact support");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteCheck, {loading: deleteLoading}] = useMutation<GraphQLResponse<null>>(GQL_DELETE_CHECKS, {
    refetchQueries: [GQL_CHECKS_QUERY2],
    awaitRefetchQueries: true,
    onError: onMutationError,
  });

  const [showPauseConfirm, setShowPauseConfirm] = useState<boolean>(false);
  const [sendResolvedNotifications, setSendResolvedNotifications] = useState<boolean>(true);
  const [updateCheckIsPaused, {loading: pauseLoading}] = useMutation<GraphQLResponse<null>>(
    GQL_UPDATE_CHECK_IS_PAUSED,
    {
      refetchQueries: [GQL_CHECKS_QUERY2],
      awaitRefetchQueries: true,
      onError: onMutationError,
    }
  );
  const handlePauseResume = async () => {
    if (isPaused || isUp) {
      await updateCheckIsPaused({variables: {ids: [id], is_paused: !isPaused, send_resolved_notifications: false}});
    } else {
      setShowPauseConfirm(true);
    }
  };

  const actions: ReactNode[] = [];
  const dropdownItems: DropdownItem[] = [];

  if (hasWriteAccess) {
    dropdownItems.push(
      <DropDownReactLink
        dataTestId="edit-check-dpd-btn"
        text="Edit Check"
        to={`/checks/${id}/edit`}
        state={{from: location}}
      />
    );
    dropdownItems.push(
      <DropdownActionItem
        text={`${isPaused ? "Resume" : "Pause"} Check`}
        onClick={handlePauseResume}
        loading={pauseLoading}
      />
    );
    dropdownItems.push(
      <DropDownReactLink
        text="Set Maintenance"
        to={`/checks/${id}/edit?${QUERY.TAB}=${baseSections.maintenance_v2.id}`}
        state={{from: location}}
      />
    );
    dropdownItems.push(<DropdownDivider />);
  }

  dropdownItems.push(<DropDownReactLink text="Alerts" to={`/alerts?service_id=${id}`} />);
  dropdownItems.push(<DropDownReactLink text="View Check Analysis" to={`/checks/${id}`} />);
  if (serviceType === MonitoringServiceType.PAGESPEED) {
    dropdownItems.push(<DropDownReactLink text="Pagespeed Report" to={`/pagespeed/report/${id}`} />);
  }
  if (serviceType !== MonitoringServiceType.GROUP) {
    dropdownItems.push(<DropDownReactLink text="View Real-Time Analysis" to={`/checks/${id}/analysis`} />);
  } else {
    dropdownItems.push(<DropDownReactLink text="Details" to={`/checks/group/${id}`} />);
  }
  dropdownItems.push(<DropdownDivider />);

  if (hasWriteAccess) {
    const DuplicateAction = (
      <DropDownReactLink text="Duplicate" to={`/checks/${id}/edit?${QUERY.DUPLICATE}=true`} state={{from: location}} />
    );

    if (serviceType === MonitoringServiceType.TRANSACTION && userHasFeature("transaction-recorder")) {
      dropdownItems.push(
        <DropDownLink text="Edit in Recorder" href={`${brand.siteURL}/transaction/recorder/?service=${id}`} />
      );
    }

    dropdownItems.push(DuplicateAction);
    dropdownItems.push(
      <DropdownActionItem text="Delete" onClick={() => setShowDeleteConfirm(true)} loading={deleteLoading} />
    );
  }

  if (hasAdminAccess) {
    dropdownItems.push(<DropDownReactLink text="Audit Log" to={`/audit/log?objectType=CHECK&search=${id}`} />);
  }

  dropdownItems.push(
    <CopyToClipboard text={`${id}`} onCopy={() => toast.success("Copied to clipboard")}>
      <Button variant={"dropdownMenuItem"} style={{cursor: "pointer"}}>
        Check ID:{" "}
        <span className="pulse-color-gray-500">
          {id} <GoCopy />
        </span>
      </Button>
    </CopyToClipboard>
  );

  actions.push(<DropdownAction id={`check-dd-${id}`} items={dropdownItems} />);

  return (
    <>
      <ActionDialog
        show={showPauseConfirm}
        size="lg"
        title="Confirm Pause and Resolve Notification?"
        confirmBtnText="Pause Check"
        loading={pauseLoading}
        onCancel={() => {
          setShowPauseConfirm(false);
          setSendResolvedNotifications(true);
        }}
        onConfirm={async () => {
          await updateCheckIsPaused({
            variables: {ids: [id], is_paused: true, send_resolved_notifications: sendResolvedNotifications},
          });
          setShowPauseConfirm(false);
          setSendResolvedNotifications(true);
        }}
      >
        <div className="px-lg-4 py-lg-4">
          <div>
            <Icon icon={FiAlertTriangle} display="inline" /> An active Alert exists for this Check.
          </div>
          <p>Pausing the Check will stop monitoring and no further updates from the Check will be sent.</p>
          <p>Would you like to send a resolve notification for the active Alert?</p>
          <div className="px-2">
            <RadioGroup
              name="send_resolved_notifications"
              value={String(sendResolvedNotifications)}
              options={[
                {value: "true", label: "Yes, send a resolve notification"},
                {value: "false", label: "No, do not send a resolve notification"},
              ]}
              onChange={(v) => setSendResolvedNotifications(v === "true")}
            />
          </div>
        </div>
      </ActionDialog>
      <ActionDialog
        show={showDeleteConfirm}
        title="Delete Check"
        loading={deleteLoading}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          const {data} = await deleteCheck({variables: {ids: [id]}});
          if (data?.response.status === ResponseStatus.SUCCESS) {
            toast.success("Check has been removed");
          }
          setShowDeleteConfirm(false);
        }}
      >
        <CheckDeleteDialogBody name={name} serviceType={serviceType} />
      </ActionDialog>
      <QuickActionsBar id={`check-action-${id}`} actions={actions} />
    </>
  );
};
